import React, { forwardRef, useId } from 'react';

import { Box } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';

type BorderProps = {
  borderRadius?: number;
  borderStyle?: 'standard' | 'gradientRotateForever' | 'gradientRotateOnce';
};
const Border: React.FC<BorderProps> = ({ borderRadius = 8, borderStyle = 'standard' }) => {
  const gradientId = useId();
  const borderColor = token('colors.border');

  const svgStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  } as const;

  const rectProps = {
    x: '0.5',
    y: '0.5',
    width: 'calc(100% - 1px)',
    height: 'calc(100% - 1px)',
    rx: borderRadius,
    ry: borderRadius,
    fill: 'none',
    stroke: borderColor,
    strokeWidth: '1',
  };

  const renderGradient = (repeatCount: string) => (
    <svg style={svgStyle}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={borderColor} />
          <stop offset="10%" stopColor={borderColor} />
          <stop offset="45%" stopColor="#f56b0f" />
          <stop offset="55%" stopColor="#f56b0f" />
          <stop offset="90%" stopColor="#ffc729" />
          <stop offset="100%" stopColor="#ffc729" />
          <animateTransform
            attributeName="gradientTransform"
            type="rotate"
            from="0 0.5 0.5"
            to="360 0.5 0.5"
            dur="6s"
            repeatCount={repeatCount}
            fill="freeze"
          />
        </linearGradient>
      </defs>
      <rect {...rectProps} stroke={`url(#${gradientId})`} />
    </svg>
  );

  switch (borderStyle) {
    case 'standard':
      return (
        <svg style={svgStyle}>
          <rect {...rectProps} />
        </svg>
      );
    case 'gradientRotateForever':
      return renderGradient('indefinite');
    case 'gradientRotateOnce':
      return renderGradient('1');
    default:
      return null as never;
  }
};

type RoundedBorderBoxProps = React.ComponentPropsWithoutRef<'div'> & BorderProps;
export const RoundedBorderBox = forwardRef<HTMLDivElement, RoundedBorderBoxProps>(
  ({ children, borderRadius, borderStyle, style, ...props }, ref) => {
    return (
      <Box
        ref={ref}
        css={{ position: 'relative', width: 'fit-content', height: 'fit-content' }}
        style={style}
        {...props}
      >
        <Border borderRadius={borderRadius} borderStyle={borderStyle} />
        <Box css={{ padding: '1px' }}>{children}</Box>
      </Box>
    );
  }
);

RoundedBorderBox.displayName = 'RoundedBorderBox';
